import { MenuIcon } from '@/components/MenuIcon';
import type { WeatherCondition, WeatherForecast } from '@/lib/types';
import { useWeather } from '@/pages/home/useWeather';
import { WidgetFrame } from '@/pages/home/widgets/WidgetFrame';

/**
 * 날씨. 기준 지점은 <b>내 대표 소속</b>이다 — 현장 소속이면 그 현장, 본사 소속이면 본사.
 * 머리글에는 그 소속명이 아니라 <b>행정구역명</b>이 나온다(`서울 성북구`).
 *
 * 지점을 고르는 것도, 기상 API 를 부르는 것도, 좌표를 지역명으로 바꾸는 것도 서버가 한다
 * ({@link useWeather}). 그래서 이 파일에는 좌표도 API 키도 없고, 받은 것을 그리기만 한다.
 *
 * 건설사가 아침에 보는 값은 기온이 아니라 "오늘 작업이 되는가"다. 그래서 강수확률·풍속·특보를
 * 앞에 두고 기온은 곁들인다. 풍속에 15m/s 표시가 붙는 것은 타워크레인 작업 중지 기준이기 때문이다.
 */

/** 타워크레인 작업 중지 기준 풍속(m/s). */
const CRANE_STOP_WIND = 15;

export function WeatherWidget() {
  const weather = useWeather();
  const forecast = weather.data;

  return (
    <WidgetFrame
      title="날씨"
      icon={forecast ? iconOf(forecast.condition) : 'sun'}
      pending={weather.isPending}
      error={weather.error}
      onRetry={weather.refetch}
      headerRight={
        forecast && (
          <span className="truncate text-ui-xs" style={{ color: 'var(--text-muted)', maxWidth: '110px' }}>
            {forecast.locationName}
          </span>
        )
      }
    >
      {forecast && (
        <>
          <div className="flex items-center gap-3">
            <span style={{ color: 'var(--accent)' }}>
              <MenuIcon name={iconOf(forecast.condition)} size={34} />
            </span>
            <div className="min-w-0">
              <div className="flex items-baseline gap-1.5">
                <span className="font-bold text-ui-3xl" style={{ color: 'var(--text-primary)', lineHeight: 1.1 }}>
                  {Math.round(forecast.temperature)}°
                </span>
                <span className="text-ui-sm" style={{ color: 'var(--text-muted)' }}>
                  체감 {Math.round(forecast.feelsLike)}°
                </span>
              </div>
              <p className="text-ui-sm" style={{ color: 'var(--text-secondary)' }}>
                {CONDITION_LABELS[forecast.condition]} · 습도 {forecast.humidity}%
              </p>
            </div>
          </div>

          {forecast.alerts.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2.5">
              {forecast.alerts.map((alert) => (
                <span key={alert} className="badge badge-red">
                  {alert}
                </span>
              ))}
            </div>
          )}

          <div className="grid grid-cols-3 gap-2 mt-3">
            <Metric label="강수확률" value={`${forecast.precipitationChance}%`} warn={forecast.precipitationChance >= 60} />
            <Metric
              label="풍속"
              value={`${forecast.windSpeed}㎧`}
              warn={forecast.windSpeed >= CRANE_STOP_WIND}
              note={forecast.windSpeed >= CRANE_STOP_WIND ? '크레인 중지' : undefined}
            />
            <Metric
              label="미세먼지"
              value={forecast.fineDust ?? '—'}
              warn={forecast.fineDust === '나쁨' || forecast.fineDust === '매우나쁨'}
            />
          </div>

          <div className="flex justify-between mt-3 pt-2.5" style={{ borderTop: '1px solid var(--border)' }}>
            {forecast.hourly.map((slot) => (
              <div key={slot.hour} className="text-center">
                <p className="text-ui-xs" style={{ color: 'var(--text-muted)' }}>{slot.hour}시</p>
                <p className="my-0.5 flex justify-center" style={{ color: 'var(--text-secondary)' }}>
                  <MenuIcon name={iconOf(slot.condition)} size={14} />
                </p>
                <p className="text-ui-xs" style={{ color: 'var(--text-secondary)' }}>{Math.round(slot.temperature)}°</p>
              </div>
            ))}
          </div>
        </>
      )}
    </WidgetFrame>
  );
}

function Metric({ label, value, warn = false, note }: { label: string; value: string; warn?: boolean; note?: string }) {
  return (
    <div
      className="px-2 py-1.5 rounded-lg"
      style={{ background: warn ? 'var(--danger-bg)' : 'var(--bg-hover)' }}
    >
      <p className="text-ui-2xs" style={{ color: 'var(--text-muted)' }}>{label}</p>
      <p className="text-ui-md" style={{ fontWeight: 700, color: warn ? 'var(--danger)' : 'var(--text-primary)' }}>
        {value}
      </p>
      {note && <p className="text-ui-2xs" style={{ color: 'var(--danger)' }}>{note}</p>}
    </div>
  );
}

const CONDITION_LABELS: Record<WeatherCondition, string> = {
  clear: '맑음',
  partly_cloudy: '구름 조금',
  cloudy: '흐림',
  rain: '비',
  shower: '소나기',
  thunderstorm: '뇌우',
  snow: '눈',
};

// 아이콘은 셋뿐이라 내리는 것은 모두 같은 그림이다. 무엇이 내리는지는 옆의 라벨이 말한다 —
// 뇌우 아이콘을 새로 그리는 것보다 글자로 갈리는 편이 작은 위젯에서 더 잘 읽힌다.
const FALLING: WeatherCondition[] = ['rain', 'shower', 'thunderstorm', 'snow'];

function iconOf(condition: WeatherForecast['condition']): string {
  if (condition === 'clear') return 'sun';
  if (FALLING.includes(condition)) return 'rain';
  return 'cloud';
}
